import type { DocumentSummary } from "@/types/document";
import type {
  AttachmentDayPage,
  DynamicFieldSummary,
  NoteAttachment,
  NoteAttachmentTag,
  NoteAutoSignOffProvider,
  NoteDetail,
  NoteGroupItem,
  NoteSignOffCandidate,
  NoteSignOffSignerGroup,
  NoteTemplateTableItem,
  NotesFetchParams,
} from "@/types/notes";
import type { Page } from "@/types/pagination";

import { getNoteDocuments } from "./note-documents";
import {
  getAttachments,
  getAutoSignOffProviders,
  getDynamicFields,
  getLatestNoteRevision,
  getNoteAttachmentTags,
  getNoteAttachments,
  getNoteDetail,
  getNoteGroups,
  getNoteRevision,
  getNoteRevisions,
  getNoteSignOffSigners,
  getNoteTemplateDetail,
  getNoteTemplates,
  getNotes,
  getPrefilledAnswers,
  getSignOffSignerGroups,
} from "./notes";

export const SIDEBAR_NOTES_PAGE_SIZE = 25;
// How often an open note checks whether a colleague has saved a newer version.
export const NOTE_LATEST_REVISION_POLL_INTERVAL = 30 * 1000;
export const NOTE_TEMPLATE_SIGNED_URL_REFRESH_INTERVAL = 60 * 60 * 1000;

export function normalizeEventIds(eventIds: string[]): string[] {
  return Array.from(new Set(eventIds.filter(Boolean))).sort();
}

function normalizeNotesFetchParams(params: NotesFetchParams): NotesFetchParams {
  return params.eventIds
    ? { ...params, eventIds: normalizeEventIds(params.eventIds) }
    : { ...params };
}

export const noteQueryKeys = {
  all: ["scribe", "notes"] as const,
  lists: () => [...noteQueryKeys.all, "list"] as const,
  list: (params: NotesFetchParams) =>
    [...noteQueryKeys.lists(), normalizeNotesFetchParams(params)] as const,
  groups: () => [...noteQueryKeys.all, "groups"] as const,
  groupIndex: (groupBy: string, params: NotesFetchParams) =>
    [
      ...noteQueryKeys.groups(),
      groupBy,
      normalizeNotesFetchParams(params),
    ] as const,
  counts: () => [...noteQueryKeys.all, "counts"] as const,
  countByDate: (params: NotesFetchParams) =>
    [...noteQueryKeys.counts(), normalizeNotesFetchParams(params)] as const,
  templates: () => [...noteQueryKeys.all, "templates"] as const,
  /** Prefix covering every template detail, so eviction and refetch match on
   * the canonical key rather than rebuilding the raw segments. */
  templateDetails: () => [...noteQueryKeys.all, "template-detail"] as const,
  templateDetail: (templateId: string) =>
    [...noteQueryKeys.templateDetails(), templateId] as const,
  dynamicFields: () => [...noteQueryKeys.all, "dynamic-fields"] as const,
  signOffSignerGroups: () =>
    [...noteQueryKeys.all, "sign-off-signer-groups"] as const,
  autoSignOffProviders: () =>
    [...noteQueryKeys.all, "auto-sign-off-providers"] as const,
  signOffSigners: (noteId: string) =>
    [...noteQueryKeys.all, "sign-off-signers", noteId] as const,
  // Seeded by the generation mutation, never fetched from the server: what that
  // one attempt extracted but could not chart. Lives outside the note detail so
  // it does not ride along on every later read of the note.
  //
  // Deliberately NOT under `all`: this entry is only ever written, and its
  // queryFn resolves to null. A broad invalidate of the note cache — which
  // note-mutation does on attachment reassignment — would refetch it and erase
  // the warning the provider has not acted on yet.
  mappingSkips: (noteId: string) =>
    ["scribe", "note-mapping-skips", noteId] as const,
  prefilledAnswers: (templateId: string, patientId: string) =>
    [...noteQueryKeys.all, "prefilled-answers", templateId, patientId] as const,
  attachments: (params: { noteId: string }) =>
    [...noteQueryKeys.all, "attachments", { ...params }] as const,
  attachment: (attachmentId: string) =>
    [...noteQueryKeys.all, "attachment", attachmentId] as const,
  attachmentsInfinite: (params: {
    patientId?: string;
    tagIds?: string[];
    optedInToSocialMedia?: boolean;
    search?: string;
  }) => [...noteQueryKeys.all, "attachments-infinite", { ...params }] as const,
  attachmentAnnotationHistory: (attachmentId: string) =>
    [
      ...noteQueryKeys.all,
      "attachment-annotation-history",
      attachmentId,
    ] as const,
  revisions: (params: { parentNoteId: string }) =>
    [...noteQueryKeys.all, "revisions", { ...params }] as const,
  revision: (params: { parentNoteId: string; version: number }) =>
    [
      ...noteQueryKeys.revisions({ parentNoteId: params.parentNoteId }),
      { ...params },
    ] as const,
  detail: (noteId: string) => [...noteQueryKeys.all, "detail", noteId] as const,
  latestRevision: (noteId: string) =>
    [...noteQueryKeys.all, "latest-revision", noteId] as const,
  tags: () => [...noteQueryKeys.all, "tags"] as const,
  noteDocuments: (noteId: string) =>
    [...noteQueryKeys.all, "note-documents", noteId] as const,
};

/**
 * With `timezone`, the list is fetched in that zone and keyed by it, for
 * callers whose date filters were built on a clock bound to that zone.
 */
function noteListQueryOptions(params: NotesFetchParams, timezone?: string) {
  const normalizedParams = normalizeNotesFetchParams(params);
  return {
    queryKey: timezone
      ? ([...noteQueryKeys.list(normalizedParams), timezone] as const)
      : noteQueryKeys.list(normalizedParams),
    queryFn: () => getNotes(normalizedParams, timezone),
    staleTime: 1000 * 60 * 2, // 2 min - reduce refetch spam when filters change rapidly
  };
}

export const noteQueryOptions = {
  list: noteListQueryOptions,
  sidebarPreview: (patientId: string) =>
    noteListQueryOptions({
      patientId,
      page: 1,
      perPage: SIDEBAR_NOTES_PAGE_SIZE,
      sortBy: "visit_date",
      sortDirection: "desc",
    }),
  listByEvents: (
    patientId: string,
    eventIds: string[],
    perPage = SIDEBAR_NOTES_PAGE_SIZE,
  ) => {
    const normalizedEventIds = normalizeEventIds(eventIds);
    return {
      ...noteListQueryOptions({
        patientId,
        eventIds: normalizedEventIds,
        page: 1,
        perPage,
      }),
      enabled: !!patientId && normalizedEventIds.length > 0,
    };
  },
  groupIndex: (
    groupBy: "patientId" | "templateId" | "providerId",
    params: NotesFetchParams,
  ) => {
    const normalizedParams = normalizeNotesFetchParams(params);
    return {
      queryKey: noteQueryKeys.groupIndex(groupBy, normalizedParams),
      queryFn: () => getNoteGroups(groupBy, normalizedParams),
      select: (data: NoteGroupItem[] | null) => data ?? [],
    };
  },
  templates: () => ({
    queryKey: noteQueryKeys.templates(),
    queryFn: () => getNoteTemplates(),
    select: (data: NoteTemplateTableItem[] | null) => data ?? [],
  }),
  templateDetail: (templateId: string) => ({
    queryKey: noteQueryKeys.templateDetail(templateId),
    queryFn: () => getNoteTemplateDetail(templateId),
    // Placeholder URLs last 65 minutes. Refresh mounted details every 60 minutes,
    // and refetch stale inactive details when they mount or regain focus.
    staleTime: NOTE_TEMPLATE_SIGNED_URL_REFRESH_INTERVAL,
    refetchInterval: NOTE_TEMPLATE_SIGNED_URL_REFRESH_INTERVAL,
    refetchOnWindowFocus: true,
  }),
  signOffSignerGroups: () => ({
    queryKey: noteQueryKeys.signOffSignerGroups(),
    queryFn: () => getSignOffSignerGroups(),
    select: (data: NoteSignOffSignerGroup[] | null) => data ?? [],
  }),
  signOffSigners: (noteId: string) => ({
    queryKey: noteQueryKeys.signOffSigners(noteId),
    queryFn: () => getNoteSignOffSigners(noteId),
    select: (data: NoteSignOffCandidate[] | null) => data ?? [],
  }),
  autoSignOffProviders: () => ({
    queryKey: noteQueryKeys.autoSignOffProviders(),
    queryFn: () => getAutoSignOffProviders(),
    select: (data: NoteAutoSignOffProvider[] | null) => data ?? [],
  }),
  dynamicFields: () => ({
    queryKey: noteQueryKeys.dynamicFields(),
    queryFn: () => getDynamicFields(),
    select: (data: DynamicFieldSummary[] | null) => data ?? [],
  }),
  attachments: (noteId: string) => ({
    queryKey: noteQueryKeys.attachments({ noteId }),
    queryFn: () => getNoteAttachments(noteId),
    select: (data: NoteAttachment[] | null) => data ?? [],
    // Photos uploaded to the gallery in another tab can be filed onto this note by the
    // server, so check again when the provider comes back to it.
    refetchOnWindowFocus: "always" as const,
  }),
  attachmentsInfinite: (
    patientId?: string,
    tagIds?: string[],
    optedInToSocialMedia?: boolean,
    search?: string,
  ) => ({
    queryKey: noteQueryKeys.attachmentsInfinite({
      patientId,
      tagIds,
      optedInToSocialMedia,
      search,
    }),
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      getAttachments({
        patientId,
        date: pageParam,
        tagIds,
        optedInToSocialMedia,
        search,
      }),
    getNextPageParam: (lastPage: AttachmentDayPage | null) =>
      lastPage?.nextDate ?? undefined,
  }),
  revisions: (parentNoteId: string) => ({
    queryKey: noteQueryKeys.revisions({ parentNoteId }),
    queryFn: () => getNoteRevisions(parentNoteId),
  }),
  revision: (parentNoteId: string, version: number) => ({
    queryKey: noteQueryKeys.revision({ parentNoteId, version }),
    queryFn: () => getNoteRevision(parentNoteId, version),
  }),
  detail: (noteId: string) => ({
    queryKey: noteQueryKeys.detail(noteId),
    queryFn: () => getNoteDetail(noteId),
    select: (data: NoteDetail | undefined) => data ?? undefined,
  }),
  // Every save writes a new version, so the version on screen never changes
  // under the provider; this is how they learn a colleague saved after them.
  // Always refetched on open and on return to the tab, since the whole point is
  // to see past what this tab last knew.
  latestRevision: (noteId: string) => ({
    queryKey: noteQueryKeys.latestRevision(noteId),
    queryFn: () => getLatestNoteRevision(noteId),
    staleTime: 0,
    refetchOnMount: "always" as const,
    refetchOnWindowFocus: "always" as const,
    refetchInterval: NOTE_LATEST_REVISION_POLL_INTERVAL,
  }),
  tags: () => ({
    queryKey: noteQueryKeys.tags(),
    queryFn: () => getNoteAttachmentTags(),
    select: (data: Page<NoteAttachmentTag> | null): NoteAttachmentTag[] =>
      data?.data ?? [],
  }),
  prefilledAnswers: (templateId: string, patientId: string) => ({
    queryKey: noteQueryKeys.prefilledAnswers(templateId, patientId),
    queryFn: () => getPrefilledAnswers(templateId, patientId),
  }),
  noteDocuments: (noteId: string) => ({
    queryKey: noteQueryKeys.noteDocuments(noteId),
    queryFn: () => getNoteDocuments(noteId),
    select: (data: DocumentSummary[] | null) => data ?? [],
  }),
};
